import {
  AppEvents,
  AuthState,
  LogoutReason,
  onAppEvent,
  SessionTimeout,
  timeConsts,
  userInterfaceSettingsAtom,
} from '@eih-netbroker/common';
import { set } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';
import { useDialog } from '../../../context/DialogContext';
import { AutoLogoutModal } from '../../../modules/core/partials/AutoLogoutModal';
import { Config } from '../../config/config';
import { primaryInstanceAtom } from '../../state/primaryInstanceAtom';
import { useLogger } from '../../utils/useLogger';
import { sseManager } from '../sse/sseManager';
import { useAuthorizationFlow } from './useAuthorizationFlow';
import { useLogout } from './useLogout';

const getLogoutAtTimestamp = (sessionTimeout: SessionTimeout): number => {
  const now = Date.now();
  if (sessionTimeout === SessionTimeout.Timeout15minutes) {
    return now + timeConsts.MINUTE_15;
  } else if (sessionTimeout === SessionTimeout.Timeout1hour) {
    return now + timeConsts.HOUR;
  } else if (sessionTimeout === SessionTimeout.Timeout4hours) {
    return now + timeConsts.HOUR_4;
  } else if (sessionTimeout === SessionTimeout.TimeoutEndOfTheDay) {
    return set(new Date(), {
      hours: 23,
      minutes: 59,
      seconds: 59,
    }).getTime();
  } else {
    return Config.NetBroker.Timeouts.ForceLogout.DefaultTimeout;
  }
};

export const useUserInactivity = () => {
  const logger = useLogger('useUserInactivity');
  const { appAuth } = useAuthorizationFlow();
  const { openDialog, closeDialogs } = useDialog();
  const { logout } = useLogout();
  const isPrimaryInstance = useAtomValue(primaryInstanceAtom);
  const sessionTimeout = useAtomValue(userInterfaceSettingsAtom).sessionTimeout;

  const logoutTimeoutId = useRef<NodeJS.Timer>(undefined);
  const warningTimeoutId = useRef<NodeJS.Timer>(undefined);
  const displayLogoutWarningAt = useRef<number>(undefined);
  const inactivityLogoutAt = useRef<number>(undefined);
  const warningModalDisplayed = useRef<boolean>(false);
  const previousIsAuthenticated = useRef<{ isAuthenticated: boolean; authStatus: AuthState } | undefined>(undefined);

  const updateTimestamps = () => {
    const sessionTimeoutTimestamp = getLogoutAtTimestamp(sessionTimeout);
    inactivityLogoutAt.current = sessionTimeoutTimestamp;
    displayLogoutWarningAt.current = sessionTimeoutTimestamp - timeConsts.MINUTE;
  };

  const logoutBecauseOfInactivity = () => {
    logout(LogoutReason.InactivityTimeout);
  };

  const openWarningModal = async () => {
    logger.debug(`Opening warning modal for inactivity timeout`);
    warningModalDisplayed.current = true;
    const res = await openDialog((close: (loggedOut: boolean) => void) => {
      return <AutoLogoutModal onCancel={loggedOut => close(loggedOut)} />;
    });
    warningModalDisplayed.current = false;
    if (!res) {
      logger.debug(`User is still active`);
      startInactivityTimer();
    }
  };

  const updateInactivityTimer = () => {
    if (logoutTimeoutId.current) {
      clearTimeout(logoutTimeoutId.current);
    }
    if (warningTimeoutId.current) {
      clearTimeout(warningTimeoutId.current);
    }
    if (!inactivityLogoutAt.current || !displayLogoutWarningAt.current) {
      throw new Error(`Timestamps are not defined`);
    }

    const now = Date.now();
    // if we are over the inactivity timeout we need to log out the user (e.g. window focus lost and updateInactivityTimer gets called after focusing the window)
    if (now > inactivityLogoutAt.current) {
      logoutBecauseOfInactivity();
    } else if (now > displayLogoutWarningAt.current && !warningModalDisplayed.current) {
      openWarningModal();
    } else {
      logoutTimeoutId.current = setTimeout(() => {
        logoutBecauseOfInactivity();
      }, inactivityLogoutAt.current - now);

      warningTimeoutId.current = setTimeout(() => {
        openWarningModal();
      }, displayLogoutWarningAt.current - now);
    }
  };

  const startInactivityTimer = () => {
    if (!isPrimaryInstance) {
      return;
    }
    updateTimestamps();
    warningModalDisplayed.current = false;
    updateInactivityTimer();
  };

  const resetInactivityTimer = () => {
    startInactivityTimer();
    sseManager.broadcastUpdateUserActivity();
  };

  const handleDocumentVisibilityChange = () => {
    if (!document.hidden && isPrimaryInstance) {
      updateInactivityTimer();
    }
  };

  const handleUserActivity = () => {
    document.body.addEventListener('keyup', resetInactivityTimer);
    document.body.addEventListener('mousedown', resetInactivityTimer);
    document.addEventListener('visibilitychange', handleDocumentVisibilityChange);
  };

  useEffect(() => {
    if (
      (!previousIsAuthenticated.current && appAuth.isAuthenticated && appAuth.authStatus === AuthState.Authenticated) ||
      (previousIsAuthenticated.current &&
        previousIsAuthenticated.current.isAuthenticated !== appAuth.isAuthenticated &&
        previousIsAuthenticated.current.authStatus !== appAuth.authStatus &&
        appAuth.isAuthenticated &&
        appAuth.authStatus === AuthState.Authenticated)
    ) {
      handleUserActivity();
      startInactivityTimer();
      previousIsAuthenticated.current = { isAuthenticated: appAuth.isAuthenticated, authStatus: appAuth.authStatus };

      if (isPrimaryInstance) {
        onAppEvent(AppEvents.UpdateUserActivityEvent.type, () => {
          if (warningModalDisplayed.current) {
            closeDialogs();
            warningModalDisplayed.current = false;
          }
          resetInactivityTimer();
        });
      }
    }

    if (!appAuth.isAuthenticated && previousIsAuthenticated.current) {
      document.body.removeEventListener('keyup', resetInactivityTimer);
      document.body.removeEventListener('mousedown', resetInactivityTimer);
      document.removeEventListener('visibilitychange', handleDocumentVisibilityChange);
      previousIsAuthenticated.current = undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- this hook is using refs so this ue only needs to run when referenced states are changing
  }, [isPrimaryInstance, appAuth, logout]);

  useEffect(() => {
    if (previousIsAuthenticated.current?.isAuthenticated) {
      handleUserActivity();
      startInactivityTimer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- this hook is using refs so this ue only needs to run when referenced states are changing
  }, [sessionTimeout, isPrimaryInstance]);
};
